// @ts-nocheck
import { ISearchPagination } from "@/integration/application/interfaces/searchPagination.interface.ts";
import { IUserSelect } from "@/integration/user/interfaces/IUser.ts";
import useUserPublic from "@integration/user/hooks/useUserPublic.ts";
import Avatar from "@/components/others/Avatar";
import React, { useState } from "react";
import { CircleCheck, Search, User, UserCheck } from "lucide-react";
import Pagination from "@/components/application/Pagination";
import Input from "@/components/form/Input/Input";
import useUser from "@integration/user/hooks/useUser.ts";
import { AnimatePresence, motion } from "framer-motion";

interface PlayerCardProps {
  player: IUserSelect;
  onSelect: (player: IUserSelect) => void;
  isSelected: boolean;
}

const PlayerCard: React.FC<PlayerCardProps> = ({ player, onSelect, isSelected }) => {
  const handleClick = () => {
    onSelect(player);
  };

  return (
    <div className="w-full">
      <motion.div 
        initial={{ opacity: 0.9, y: 5 }}
        animate={{ opacity: 1, y: 0 }}
        whileTap={{ scale: 0.99 }}
        transition={{ duration: 0.2 }}
        className={`w-full rounded-xl border-box p-3 bo cursor-pointer relative overflow-hidden
          ${isSelected 
            ? 'bg-green-500/20 border border-green-500 shadow-md shadow-green-500/10' 
            : 'bg-gray-800/80 border border-gray-700 hover:border-green-500/50 hover:bg-gray-800'
          }`}
        onClick={handleClick}
      >
        <div className="flex items-center w-full gap-3">
          <div className="relative">
            <Avatar 
              src={player?.picture} 
              children={player?.name.charAt(0)} 
              sizeClass='w-12 h-12 flex-shrink-0'
              className={`${isSelected ? 'border-green-500' : 'border-gray-600'}`}
            />
            {isSelected && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5 border-2 border-gray-800"
              >
                <UserCheck className='w-3.5 h-3.5 text-white' />
              </motion.div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-white truncate">
              {player.name}
            </h3>
            <p className="text-xs text-gray-400 truncate">
              Jogador
            </p>
          </div>

          {isSelected ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex-shrink-0 text-green-500"
            >
              <CircleCheck className='w-5 h-5' />
            </motion.div>
          ) : (
            <div className="flex-shrink-0 text-gray-500 opacity-50">
              <User className='w-5 h-5' />
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

interface ListPlayersProps {
  onPlayerSelect: (players: IUserSelect[], message?: string) => void;
  selectedPlayers: IUserSelect[];
  shouldReset: boolean;
  currentMessage?: string;
  multiSelect?: boolean;
}

const ListPlayers: React.FC<ListPlayersProps> = ({ onPlayerSelect, selectedPlayers, shouldReset, currentMessage = '', multiSelect = false }) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [message, setMessage] = useState(currentMessage);
  const scrollThreshold = 100;

  const {userAuth} = useUser();
  const { searchUsersPaginationSelect } = useUserPublic(userAuth?.id  || '');

  const searchParams: ISearchPagination = {
    order: "asc",
    limit: 80,  
    search: search,
    page: page,
    entityId: "",
  };

  const { players, data, isFetching, isLoading } = searchUsersPaginationSelect(searchParams);

  // Reset quando shouldReset ou search mudar
  React.useEffect(() => {
    if (shouldReset || search) {
      setPage(1);
    }
  }, [shouldReset, search]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= (data?.totalPage || 1)) {
      setPage(newPage);
    }
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    const distanceToBottom = scrollHeight - scrollTop - clientHeight;
    const distanceToTop = scrollTop;

    // Só carrega mais se estiver realmente próximo do final
    if (distanceToBottom <= scrollThreshold && !isLoadingMore && !isFetching && distanceToBottom > 0) {
      if (page < (data?.totalPage || 1)) {
        setIsLoadingMore(true);
        handlePageChange(page + 1);
      }
    }

    // Só carrega anterior se estiver realmente no topo
    if (distanceToTop <= scrollThreshold && !isLoadingMore && !isFetching && distanceToTop === 0) {
      if (page > 1) {
        setIsLoadingMore(true);
        handlePageChange(page - 1);
      }
    }
  };

  // Reseta o estado de loading quando os dados são carregados
  React.useEffect(() => {
    if (!isFetching) {
      setIsLoadingMore(false);
    }
  }, [isFetching]);

  const handlePlayerSelect = (player: IUserSelect) => {
    const isSelected = selectedPlayers.some(p => p.id === player.id);
    let newSelectedPlayers: IUserSelect[];

    if (multiSelect) {
      // Multiple selection mode
      if (isSelected) {
        // Remove player if already selected
        newSelectedPlayers = selectedPlayers.filter(p => p.id !== player.id);
      } else {
        // Add player to selection
        newSelectedPlayers = [...selectedPlayers, player];
      }
    } else {
      // Single selection mode
      if (isSelected) {
        newSelectedPlayers = [];
      } else {
        newSelectedPlayers = [player]; // Only one player can be selected
      }
    }

    onPlayerSelect(newSelectedPlayers, message);
  };

  return (
    <>
    <div className="flex justify-between items-center flex-shrink-0">
        <div className="w-full">
          <Input
            id="search"
            iconLeft={<Search className="text-green-500" />}
            type="text"
            placeholder="Buscar jogadores por nome..."
            className="w-full p-2.5 bg-gray-800 border-gray-700 text-white rounded-lg focus:border-green-500 focus:ring-green-500/30"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="flex flex-col w-full gap-5 bg-gray-900/50 border border-gray-700 rounded-xl p-4 h-[400px] overflow-hidden shadow-lg">
    
    <div 
      className="flex-1 scrollbar-custom flex flex-col gap-2.5 overflow-y-auto pr-2"
      onScroll={handleScroll}
    >
      <h3 className="text-white text-sm font-medium mb-2 flex items-center gap-2">
            <User size={16} className="text-green-500" />
            <span>{multiSelect ? "Selecione os jogadores" : "Selecione apenas um jogador"}</span>
          </h3>
          
      <AnimatePresence>
        {players?.map((player, index) => (
          <motion.div
            key={player.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2, delay: index * 0.03 }}
          >
            <PlayerCard
              player={player}
              onSelect={handlePlayerSelect}
              isSelected={selectedPlayers.some(p => p.id === player.id)}
            />
          </motion.div>
        ))}
      </AnimatePresence>

      {isLoadingMore && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex items-center justify-center w-full text-green-500 py-3 bg-gray-800/50 rounded-lg border border-gray-700"
        >
          <div className="animate-spin mr-2">
            <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          Carregando mais jogadores...
        </motion.div>
      )}

      {isLoading && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center w-full text-green-500 py-8 bg-gray-800/30 rounded-lg border border-gray-700"
        >
          <div className="animate-spin mb-4">
            <svg className="w-10 h-10" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <p className="text-white font-medium">Buscando jogadores...</p>
          <p className="text-xs text-gray-400 mt-1">Aguarde um momento</p>
        </motion.div>
      )}

      {!isLoading && (!players || players.length === 0) && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col items-center justify-center w-full text-gray-400 py-8 bg-gray-800/30 rounded-lg border border-gray-700"
        >
          <User size={40} className="text-gray-500 mb-2" />
          <p>Nenhum jogador encontrado</p>
          <p className="text-xs text-gray-500 mt-1">Tente uma busca diferente</p>
        </motion.div>
      )}
    </div>

    <div className="flex-shrink-0 pt-2 border-t border-gray-700">
      <Pagination
        currentPage={page}
        totalPages={data?.totalPage || 1}
        onPageChange={handlePageChange}
      />
    </div>
  </div>
    </>
  );
};

export default ListPlayers;
